import { CodeOutlined } from '@ant-design/icons';
import { WebContainerProcess } from '@webcontainer/api';
import { Button, Space } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Terminal as XTerm } from 'xterm';
import 'xterm/css/xterm.css';
// 修改这些导入路径
import { useSession } from '../contexts/SessionContext';
import { useWebContainer } from '../contexts/WebContainerContext';

interface TerminalProps {
  active: boolean;
  onTerminalReady: (terminal: XTerm) => void;
}

const Terminal: React.FC<TerminalProps> = ({ active, onTerminalReady }) => {
  const terminalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!terminalRef.current || !onTerminalReady) return;

    // 创建终端实例
    const terminal = new XTerm({
      cursorBlink: true,
      rows: 24,
      cols: 60,
      theme: {
        background: '#1e1e1e',
        foreground: '#f0f0f0',
      },
      fontSize: 14,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
    });

    // 打开终端
    terminal.open(terminalRef.current);
    onTerminalReady(terminal);
  }, [onTerminalReady]);

  return (
    <div
      ref={terminalRef}
      style={{
        display: active ? 'block' : 'none',
      }}
    />
  );
};

const TerminalPanel: React.FC = () => {
  const [terminalVisible, setTerminalVisible] = useState<boolean>(false);
  const [terminalCount, setTerminalCount] = useState<number>(0);
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const { startProcess, writer, processOutput } = useSession();
  const { webContainer } = useWebContainer();
  const outputRef = useRef<ReadableStream<string> | null>(null);

  const connect = useCallback((process: WebContainerProcess, terminal: XTerm) => {
    const input = process.input.getWriter();
    const { output } = process;
    output.pipeTo(
      new WritableStream({
        write(data) {
          terminal.write(data);
        },
      })
    );
    terminal.onData(data => {
      input.write(data);
    });
  }, []);

  const handleTerminalReady = useCallback(
    async (terminal: XTerm) => {
      if (!webContainer) {
        return;
      }
      const process = await webContainer.spawn('/bin/jsh', ['--osc'], {
        terminal: {
          cols: 60,
          rows: 24,
        },
      });

      connect(process, terminal);
    },
    [webContainer, connect]
  );

  const mainProcess = useCallback(
    async (terminal: XTerm) => {
      if (!startProcess || !writer || !processOutput) {
        return;
      }
      const output = processOutput.createStream();
      outputRef.current = output;
      output.pipeTo(
        new WritableStream({
          write(data) {
            terminal.write(data);
          },
        })
      );
      terminal.onData(data => {
        writer.write(data);
      });
    },
    [startProcess, writer]
  );

  return (
    <>
      <Button
        type="primary"
        shape="circle"
        size="large"
        icon={<CodeOutlined />}
        onClick={() => setTerminalVisible(!terminalVisible)}
        style={{
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        }}
      />
      <div
        style={{
          position: 'fixed',
          bottom: '80px',
          right: '20px',
          width: '600px',
          height: '400px',
          backgroundColor: '#1e1e1e',
          color: '#f0f0f0',
          borderRadius: '5px',
          zIndex: 1000,
          display: terminalVisible ? 'flex' : 'none',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          overflow: 'hidden',
        }}
      >
        <Space
          direction="vertical"
          style={{
            padding: '8px 12px',
            backgroundColor: '#333',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          {Array.from({ length: terminalCount + 1 }, (_, index) => (
            <Button
              key={index}
              type={index === activeIndex ? 'primary' : 'text'}
              size="small"
              onClick={() => setActiveIndex(index)}
              style={{ color: '#f0f0f0' }}
            >
              {index === 0 ? 'Main' : `Terminal ${index + 1}`}
            </Button>
          ))}
          <Button
            type="text"
            size="small"
            onClick={() => setTerminalCount(pre => pre + 1)}
            style={{ color: '#f0f0f0' }}
          >
            +
          </Button>
        </Space>
        {startProcess && <Terminal active={activeIndex === 0} onTerminalReady={mainProcess} />}
        {Array.from({ length: terminalCount }, (_, index) => {
          const isActive = index + 1 === activeIndex;
          return <Terminal key={index} active={isActive} onTerminalReady={handleTerminalReady} />;
        })}
      </div>
    </>
  );
};
export default TerminalPanel;
